import { ref, computed } from "vue";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "@/firebase";

interface TenantInfo {
  siteId: string;
  businessName: string;
  active?: boolean;
  adminUids: string[];
  adminEmails: string[];
  logoUrl?: string | null;
  googleSpaceWebhook?: string | null;
  notificationEmail?: string | null;
  widgetConfig: {
    title: string;
    subtitle: string;
    themeColor: string;
    eventTypes: string[];
    targetSelector?: string | null;
    formFields?: Array<{ id: string; label: string; type: string; required: boolean; placeholder?: string }> | null;
    aiEnabled?: boolean;
  };
}

const currentUser = ref<User | null>(null);
const authLoading = ref(true);
const authError = ref<string | null>(null);
// Plan SC: Tenant Admin은 자신의 siteId만 조회, Super Admin은 null (전체 조회)
const currentSiteId = ref<string | null>(null);
const tenantInfo = ref<TenantInfo | null>(null);
// All tenants this user belongs to (for org switcher)
const allUserTenants = ref<TenantInfo[]>([]);

const SITE_ID_STORAGE_KEY = "ck_admin_site_id";

// signInWithEmail/signInWithGoogle이 Firebase 인증 자체는 성공해도, 아래 onAuthStateChanged의
// 테넌트 조회(비동기)가 끝나기 전엔 currentUser가 비어있어 리다이렉트가 무산되는 문제가 있었다.
// 이 핸들러가 한 사이클 완료될 때마다 대기 중인 호출자에게 알려준다.
let authReadyResolvers: Array<() => void> = [];
function waitForAuthReady(): Promise<void> {
  if (currentUser.value) return Promise.resolve();
  return new Promise((resolve) => { authReadyResolvers.push(resolve); });
}
function flushAuthReady(): void {
  authReadyResolvers.forEach((r) => r());
  authReadyResolvers = [];
}

onAuthStateChanged(auth, async (user) => {
  if (!user) {
    currentUser.value = null;
    currentSiteId.value = null;
    tenantInfo.value = null;
    allUserTenants.value = [];
    authLoading.value = false;
    flushAuthReady();
    return;
  }

  const email = user.email ?? "";
  const isSuperAdmin = email.endsWith("@congkong.net");

  if (isSuperAdmin) {
    currentUser.value = user;
    currentSiteId.value = null;
    tenantInfo.value = null;
    allUserTenants.value = [];
    authError.value = null;
  } else {
    try {
      const tenantSnap = await getDocs(query(
        collection(db, "tenants"),
        where("adminUids", "array-contains", user.uid)
      ));

      if (!tenantSnap.empty) {
        const tenants = tenantSnap.docs.map(d => d.data() as TenantInfo);
        allUserTenants.value = tenants;

        const savedSiteId = localStorage.getItem(SITE_ID_STORAGE_KEY);
        const preferred = savedSiteId ? tenants.find(t => t.siteId === savedSiteId) : null;
        const tenant = preferred ?? tenants[0];

        currentUser.value = user;
        currentSiteId.value = tenant.siteId;
        tenantInfo.value = tenant;
        localStorage.setItem(SITE_ID_STORAGE_KEY, tenant.siteId);
        authError.value = null;
      } else {
        // UID 미등록 → adminEmails 기반으로 자동 연결 시도 (첫 로그인)
        try {
          const fn = httpsCallable<unknown, { ok: boolean; tenants: TenantInfo[] }>(functions, "linkTenantAccount");
          const result = await fn({});
          if (result.data.ok && result.data.tenants.length > 0) {
            const tenants = result.data.tenants;
            allUserTenants.value = tenants;
            const savedSiteId = localStorage.getItem(SITE_ID_STORAGE_KEY);
            const preferred = savedSiteId ? tenants.find(t => t.siteId === savedSiteId) : null;
            const tenant = preferred ?? tenants[0];
            currentUser.value = user;
            currentSiteId.value = tenant.siteId;
            tenantInfo.value = tenant;
            localStorage.setItem(SITE_ID_STORAGE_KEY, tenant.siteId);
            authError.value = null;
          }
        } catch {
          // /invite 페이지는 로그아웃하지 않음
          const isOnInvitePage = typeof window !== "undefined" && window.location.pathname.startsWith("/invite");
          if (isOnInvitePage) {
            currentUser.value = user;
            currentSiteId.value = null;
            tenantInfo.value = null;
            authError.value = null;
          } else {
            await firebaseSignOut(auth);
            currentUser.value = null;
            currentSiteId.value = null;
            tenantInfo.value = null;
            authError.value = "등록되지 않은 계정입니다. 관리자에게 계정 추가를 요청해주세요.";
          }
        }
      }
    } catch (err) {
      console.error("[Auth] Tenant lookup failed:", err);
      currentUser.value = user;
      currentSiteId.value = null;
      authError.value = null;
    }
  }

  authLoading.value = false;
  flushAuthReady();
});

export function useAuth() {
  const isAuthenticated = computed(() => currentUser.value !== null);
  const userEmail = computed(() => currentUser.value?.email ?? null);
  const userName = computed(
    () => currentUser.value?.displayName ?? currentUser.value?.email ?? "Admin"
  );
  const userPhoto = computed(() => currentUser.value?.photoURL ?? null);
  const isSuperAdmin = computed(() => {
    if (!currentUser.value) return false;
    if (currentSiteId.value !== null) return false;
    return currentUser.value.email?.endsWith("@congkong.net") ?? false;
  });

  async function signInWithGoogle(): Promise<void> {
    authError.value = null;
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      await waitForAuthReady();
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return;
      authError.value = "Google 로그인에 실패했습니다. 잠시 후 다시 시도해주세요.";
    }
  }

  async function signInWithEmail(email: string, password: string): Promise<void> {
    authError.value = null;
    try {
      await signInWithEmailAndPassword(auth, email, password);
      await waitForAuthReady();
    } catch (err: unknown) {
      if (err instanceof Error) {
        const code = (err as { code?: string }).code;
        if (code === "auth/user-not-found" || code === "auth/wrong-password" || code === "auth/invalid-credential") {
          authError.value = "이메일 또는 비밀번호가 올바르지 않습니다.";
        } else {
          authError.value = "로그인에 실패했습니다. 잠시 후 다시 시도해주세요.";
        }
      }
    }
  }

  function switchTenant(siteId: string): void {
    const t = allUserTenants.value.find(t => t.siteId === siteId);
    if (!t) return;
    currentSiteId.value = siteId;
    tenantInfo.value = t;
    localStorage.setItem(SITE_ID_STORAGE_KEY, siteId);
  }

  async function signOut(): Promise<void> {
    localStorage.removeItem(SITE_ID_STORAGE_KEY);
    await firebaseSignOut(auth);
    currentUser.value = null;
    currentSiteId.value = null;
    tenantInfo.value = null;
    allUserTenants.value = [];
  }

  return {
    currentUser,
    isAuthenticated,
    authLoading,
    authError,
    userEmail,
    userName,
    userPhoto,
    isSuperAdmin,
    currentSiteId,
    tenantInfo,
    allUserTenants,
    switchTenant,
    signInWithGoogle,
    signInWithEmail,
    signOut,
  };
}
